import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LedgerHealthCard } from "../components/LedgerHealthCard";
import { ShieldCheck, KeyRound, CreditCard, Wallet, Users, TrendingUp, DollarSign } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">{value ?? "—"}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/stats").then((res) => setStats(res.data)).catch(() => {});
    api.get("/analytics", { params: { days: 14 } }).then((res) => setAnalytics(res.data)).catch(() => {});
  }, []);

  const isAdmin = user?.role === "admin";
  const chartData = (analytics?.series || []).map((d) => ({
    ...d,
    label: d.date.slice(5),
  }));

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">
          {isAdmin ? "Administrator Console" : "Merchant Vault"}
        </div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Security Dashboard</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isAdmin
            ? "Oversight across all registered merchants and their encrypted vault records."
            : `Welcome back, ${user?.name || user?.email}. All your secrets are encrypted and shown masked.`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {isAdmin && (
          <StatCard icon={Users} label="Merchants" value={stats?.merchants} accent="text-accent" />
        )}
        <StatCard icon={CreditCard} label="Card Tokens" value={stats?.tokens} accent="text-primary" />
        <StatCard icon={KeyRound} label="Credentials" value={stats?.credentials} accent="text-accent" />
        <StatCard icon={Wallet} label="Wallet Keys" value={stats?.wallets} accent="text-primary" />
      </div>

      <LedgerHealthCard />

      <div className="rounded-xl border border-border bg-card p-6" data-testid="analytics-chart">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-medium">
            <TrendingUp className="h-4 w-4 text-primary" /> Token activity · last 14 days
          </h3>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span><span className="font-semibold text-primary">{analytics?.total_created ?? 0}</span> created</span>
            <span><span className="font-semibold text-accent">{analytics?.total_charged ?? 0}</span> charged</span>
            <span><span className="font-semibold text-foreground">${analytics?.total_volume?.toFixed(2) ?? "0.00"}</span> volume</span>
          </div>
        </div>
        <div className="mt-4 h-64 w-full">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gCharged" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "hsl(var(--popover-foreground))",
                }}
              />
              <Area type="monotone" dataKey="created" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#gCreated)" name="Created" />
              <Area type="monotone" dataKey="charged" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#gCharged)" name="Charged" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6" data-testid="volume-chart">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-medium">
            <DollarSign className="h-4 w-4 text-accent" /> Charge volume · last 14 days
          </h3>
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">${analytics?.total_volume?.toFixed(2) ?? "0.00"}</span> total processed
          </div>
        </div>
        <div className="mt-4 h-64 w-full">
          {!analytics ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground" data-testid="volume-loading">
              Loading charge data…
            </div>
          ) : analytics.total_volume === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-center" data-testid="volume-empty">
              <DollarSign className="h-8 w-8 text-muted-foreground opacity-40" />
              <p className="text-sm font-medium text-muted-foreground">No charges yet</p>
              <p className="text-xs text-muted-foreground">Use a token in the Card Vault to record your first charge.</p>
            </div>
          ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                formatter={(v) => [`$${Number(v).toFixed(2)}`, "Volume"]}
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "hsl(var(--popover-foreground))",
                }}
              />
              <Bar dataKey="volume" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} name="Volume" />
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>

      {isAdmin && analytics?.by_merchant?.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="by-merchant-chart">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-medium">
            <Users className="h-4 w-4 text-primary" /> Top merchants by charge volume
          </h3>
          <div className="w-full" style={{ height: Math.max(180, analytics.by_merchant.length * 46) }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
              <BarChart
                data={analytics.by_merchant}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="merchant" width={130} stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                  formatter={(v, n, p) => [`$${Number(v).toFixed(2)} · ${p.payload.charges} charges`, "Volume"]}
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "hsl(var(--popover-foreground))",
                  }}
                />
                <Bar dataKey="volume" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} name="Volume" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-medium">Zero-exposure encryption</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              Every API secret, card number, and private key is encrypted with AES-256 before it
              touches storage. After saving, raw values are never returned to the browser — the vault
              only ever renders masked representations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
